import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PaginationContextType } from '../../types/paginationContext.types';

export const PaginationContext = createContext<
  PaginationContextType | undefined
>(undefined);

interface PaginationProviderProps {
  children: ReactNode;
  defaultItemsPerPage: number;
  onPageChangeTrackingFn?: ({
    totalPageCount,
    currentPageIndex,
    selectedPageIndex,
  }: {
    totalPageCount: number;
    currentPageIndex: number;
    selectedPageIndex: number;
  }) => void;
}

export const PaginationProvider = ({
  children,
  defaultItemsPerPage,
  onPageChangeTrackingFn,
}: PaginationProviderProps) => {
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [numberOfItemsPerPage] = useState<number>(defaultItemsPerPage);
  const totalPageCount = useMemo(
    () => Math.ceil(totalItemCount / numberOfItemsPerPage),
    [numberOfItemsPerPage, totalItemCount]
  );

  const onSelectPage = useCallback(
    (index: number) => {
      setCurrentPageIndex(index);

      onPageChangeTrackingFn?.({
        totalPageCount,
        currentPageIndex,
        selectedPageIndex: index,
      });
    },
    [currentPageIndex, onPageChangeTrackingFn, totalPageCount]
  );

  const paginationContextValues = useMemo(
    () => ({
      currentPageIndex,
      totalPageCount,
      totalItemCount,
      setTotalItemCount,
      numberOfItemsPerPage,
      onSelectPage,
    }),
    [
      currentPageIndex,
      numberOfItemsPerPage,
      onSelectPage,
      totalItemCount,
      totalPageCount,
    ]
  );

  return (
    <PaginationContext.Provider value={paginationContextValues}>
      {children}
    </PaginationContext.Provider>
  );
};

export const usePaginationContext = () => {
  const context = useContext(PaginationContext);
  if (context === undefined) {
    throw new Error(
      'usePaginationContext must be used within a PaginationProvider'
    );
  }
  return context;
};
